import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from 'components/ui/Header';
import Footer from 'components/ui/Footer';
import FloatingActionButtons from 'components/ui/FloatingActionButtons';
import Image from 'components/AppImage';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';

const ResortsOverview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeFilter, setActiveFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // grid or list

  const selectedResort = location?.state?.selectedResort;

  const resorts = [
    {
      id: 1,
      name: "Dandeli Riverside Resort",
      category: "luxury",
      images: [
        "https://images.unsplash.com/photo-1571896349842-33c89424de2d",
        "https://images.unsplash.com/photo-1566073771259-6a8506099945",
        "https://images.unsplash.com/photo-1551632811-561732d1e306"
      ],
      description: "Experience ultimate luxury by the pristine Kali River with premium amenities, stunning views, and world-class service in the heart of Dandeli's wilderness.",
      fullDescription: "Our flagship property offers the perfect blend of luxury and nature. Located on the banks of the Kali River, this resort features spacious riverside cottages, a multi-level swimming pool, spa services, and direct access to water sports. The property spans 15 acres of landscaped gardens with over 50 luxury accommodations.",
      rating: 4.8,
      reviews: 324,
      priceRange: "₹2,499 - ₹4,999",
      location: "Riverside Location, 2km from Dandeli Town",
      features: ["Riverside Location", "Swimming Pool", "Multi-Cuisine Restaurant", "Adventure Sports", "Spa & Wellness", "Conference Hall"],
      amenities: ["WiFi", "AC", "Room Service", "Laundry", "Parking", "Airport Transfer"],
      roomTypes: ["Deluxe Riverside Cottage", "Premium River View", "Luxury Suite", "Family Villa"],
      marketing: "Most Popular Choice - Book now just at ₹2,499/-",
      awards: ["TripAdvisor Excellence Award 2024", "Best Adventure Resort Karnataka"],
      capacity: "120 Guests",
      established: "2018",
      certifications: ["ISO 9001", "Green Tourism"]
    },
    {
      id: 2,
      name: "Forest Canopy Resort",
      category: "eco-friendly",
      images: [
        "https://images.unsplash.com/photo-1566073771259-6a8506099945",
        "https://images.unsplash.com/photo-1571896349842-33c89424de2d",
        "https://images.unsplash.com/photo-1549366021-9f761d040a94"
      ],
      description: "Sustainable eco-luxury in the treetops with innovative treehouse accommodations, organic dining, and carbon-neutral operations amidst pristine forest.",
      fullDescription: "India's first carbon-neutral treehouse resort, built using sustainable materials and powered by renewable energy. Each treehouse is uniquely designed with panoramic forest views, eco-friendly amenities, and minimal environmental impact. Perfect for nature enthusiasts and eco-conscious travelers.",
      rating: 4.7,
      reviews: 198,
      priceRange: "₹1,999 - ₹3,499",
      location: "Forest Canopy, 5km from Dandeli Wildlife Sanctuary",
      features: ["Treehouse Rooms", "Nature Walks", "Bird Watching", "Organic Meals", "Solar Power", "Rainwater Harvesting"],
      amenities: ["Eco-Friendly Toiletries", "Organic Garden", "Nature Library", "Yoga Deck", "Composting", "Wildlife Observatory"],
      roomTypes: ["Canopy Treehouse", "Forest Cottage", "Eco Pod", "Naturalist Suite"],
      marketing: "Eco-Friendly Paradise - Starting from ₹1,999/-",
      awards: ["Green Hotel Award 2024", "Sustainable Tourism Excellence"],
      capacity: "80 Guests",
      established: "2020",
      certifications: ["Green Key Certification", "EarthCheck Silver"]
    },
    {
      id: 3,
      name: "Adventure Base Camp",
      category: "adventure",
      images: [
        "https://images.unsplash.com/photo-1551632811-561732d1e306",
        "https://images.unsplash.com/photo-1544551763-46a013bb70d5",
        "https://images.unsplash.com/photo-1549366021-9f761d040a94"
      ],
      description: "The ultimate destination for thrill-seekers with direct access to all adventure activities, professional guides, and specialized equipment rental services.",
      fullDescription: "Designed specifically for adventure enthusiasts, this base camp style resort offers no-frills comfort with maximum adventure access. Features include equipment storage, gear rental, activity briefing areas, and direct trail access. Perfect for groups, corporate teams, and serious adventurers.",
      rating: 4.9,
      reviews: 445,
      priceRange: "₹1,799 - ₹2,999",
      location: "Adventure Hub, Central Dandeli Location",
      features: ["Adventure Hub", "Camping Options", "Guided Tours", "Equipment Rental", "Training Areas", "Emergency Support"],
      amenities: ["Gear Storage", "Equipment Cleaning", "First Aid Station", "Activity Booking", "Transport Service", "Group Facilities"],
      roomTypes: ["Adventure Cabin", "Base Camp Tent", "Group Dormitory", "Guide Quarters"],
      marketing: "Adventure Headquarters - Limited time offer ₹1,799/-",
      awards: ["Best Adventure Resort India 2024", "Outdoor Excellence Award"],
      capacity: "200 Guests",
      established: "2019",
      certifications: ["Adventure Tourism Safety", "Rescue Operations Certified"]
    },
    {
      id: 4,
      name: "Heritage Dandeli Palace",
      category: "heritage",
      images: [
        "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b",
        "https://images.unsplash.com/photo-1571896349842-33c89424de2d",
        "https://images.unsplash.com/photo-1566073771259-6a8506099945"
      ],
      description: "Regal grandeur meets wilderness in this restored colonial palace featuring royal suites, heritage dining, and classical Indian hospitality traditions.",
      fullDescription: "A beautifully restored colonial-era building that served as a hunting lodge for the Maharajas. Now a luxury heritage hotel featuring antique furnishings, royal suites, traditional architecture, and modern amenities. Each room tells a story of India's rich heritage combined with contemporary comfort.",
      rating: 4.6,
      reviews: 167,
      priceRange: "₹3,999 - ₹7,499",
      location: "Heritage District, Old Dandeli Road",
      features: ["Heritage Architecture", "Royal Suites", "Traditional Dining", "Cultural Programs", "Antique Collections", "Palace Gardens"],
      amenities: ["Butler Service", "Heritage Tours", "Classical Music", "Traditional Spa", "Horse Riding", "Royal Library"],
      roomTypes: ["Maharaja Suite", "Heritage Room", "Colonial Chamber", "Royal Villa"],
      marketing: "Royal Experience - Book your palace stay from ₹3,999/-",
      awards: ["Heritage Hotel of the Year 2023", "Cultural Tourism Award"],
      capacity: "60 Guests",
      established: "1920 (Restored 2021)",
      certifications: ["Heritage Conservation", "Cultural Authenticity"]
    },
    {
      id: 5,
      name: "Dandeli Family Resort",
      category: "family",
      images: [
        "https://images.unsplash.com/photo-1571896349842-33c89424de2d",
        "https://images.unsplash.com/photo-1566073771259-6a8506099945",
        "https://images.unsplash.com/photo-1551632811-561732d1e306"
      ],
      description: "Specially designed for families with children's play areas, safe adventure activities, family suites, and dedicated kids' entertainment programs.",
      fullDescription: "The perfect family destination with specially designed amenities for all ages. Features include kids' clubs, family activity areas, safe play zones, educational nature programs, and comfortable family accommodations. All activities are designed with family safety and enjoyment in mind.",
      rating: 4.5,
      reviews: 289,
      priceRange: "₹2,199 - ₹4,299",
      location: "Family Zone, Near Dandeli Town Center",
      features: ["Kids Play Area", "Family Activities", "Safe Adventures", "Children\'s Pool", "Educational Programs", "Babysitting Services"],
      amenities: ["Family Rooms", "High Chairs", "Cribs Available", "Kids Menu", "Game Room", "Medical Support"],
      roomTypes: ["Family Suite", "Connecting Rooms", "Triple Occupancy", "Kids Themed Room"],
      marketing: "Perfect Family Getaway - Special family packages from ₹2,199/-",
      awards: ["Family-Friendly Resort Award 2024", "Child Safety Excellence"],
      capacity: "150 Guests",
      established: "2017",
      certifications: ["Child Safety Certified", "Family Tourism Standard"]
    },
    {
      id: 6,
      name: "Dandeli Budget Retreat",
      category: "budget",
      images: [
        "https://images.unsplash.com/photo-1551632811-561732d1e306",
        "https://images.unsplash.com/photo-1566073771259-6a8506099945",
        "https://images.unsplash.com/photo-1549366021-9f761d040a94"
      ],
      description: "Affordable comfort without compromising on the Dandeli experience. Clean, comfortable accommodations with access to all major attractions and activities.",
      fullDescription: "Proving that great experiences don't require a big budget, this resort offers clean, comfortable accommodations with all essential amenities. Perfect for backpackers, student groups, and budget-conscious travelers who want to experience Dandeli's adventures without the luxury price tag.",
      rating: 4.3,
      reviews: 423,
      priceRange: "₹999 - ₹1,899",
      location: "Budget Zone, Walking Distance to Main Attractions",
      features: ["Budget-Friendly", "Clean Accommodations", "Shared Facilities", "Basic Meals", "Activity Booking", "Transport Assistance"],
      amenities: ["Free WiFi", "Common Areas", "Shared Kitchen", "Laundry Service", "Luggage Storage", "24/7 Security"],
      roomTypes: ["Standard Room", "Dormitory", "Budget Cottage", "Shared Accommodation"],
      marketing: "Best Value Experience - Starting just ₹999/-",
      awards: ["Best Budget Resort 2024", "Value for Money Excellence"],
      capacity: "180 Guests",
      established: "2016",
      certifications: ["Budget Tourism Standard", "Cleanliness Certified"]
    }
  ];

  const categories = [
    { id: 'all', label: 'All Resorts', icon: 'Home' },
    { id: 'luxury', label: 'Luxury', icon: 'Star' },
    { id: 'eco-friendly', label: 'Eco-Friendly', icon: 'Leaf' },
    { id: 'adventure', label: 'Adventure', icon: 'Mountain' },
    { id: 'heritage', label: 'Heritage', icon: 'Crown' },
    { id: 'family', label: 'Family', icon: 'Users' },
    { id: 'budget', label: 'Budget', icon: 'DollarSign' }
  ];

  const filteredResorts = activeFilter === 'all' 
    ? resorts 
    : resorts?.filter(resort => resort?.category === activeFilter);

  const handleBookNow = (resort) => {
    navigate('/booking-engine', { state: { selectedResort: resort } });
  };

  const handleViewDetails = (resort) => {
    navigate('/package-details', { state: { selectedResort: resort } });
  };

  const handleWhatsAppCustom = (resort) => {
    const message = encodeURIComponent(`Hi! I'm interested in ${resort?.name}. Can you provide me with a custom package quote and availability details?`);
    window.open(`https://wa.me/918277385225?text=${message}`, '_blank');
  };

  const renderStars = (rating) => {
    return [...Array(5)]?.map((_, index) => (
      <Icon
        key={index}
        name="Star"
        size={14}
        className={index < Math.floor(rating) ? 'text-warning' : 'text-gray-300'}
      />
    ));
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Header />
      {/* Hero Section */}
      <section className="pt-16 bg-gradient-to-r from-primary to-secondary text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              Explore Our Premium Resorts
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto font-body">
              Choose from our carefully curated collection of resorts, each offering unique experiences in Dandeli's pristine wilderness
            </p>
            {selectedResort && (
              <div className="mt-6 bg-white/10 backdrop-blur-sm rounded-lg p-4 inline-block">
                <p className="text-accent font-heading font-semibold">
                  ✨ You're viewing details for: {selectedResort?.name}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
      {/* Filters and View Toggle */}
      <section className="py-8 bg-muted border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            {/* Category Filters */}
            <div className="flex flex-wrap items-center gap-2">
              {categories?.map((category) => (
                <button
                  key={category?.id}
                  onClick={() => setActiveFilter(category?.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-organic ${
                    activeFilter === category?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background text-text-primary hover:bg-primary/10 hover:text-primary'
                  }`}
                >
                  <Icon name={category?.icon} size={16} />
                  <span>{category?.label}</span>
                </button>
              ))}
            </div>

            {/* View Toggle and Results Count */}
            <div className="flex items-center space-x-4">
              <span className="text-text-secondary font-body">
                {filteredResorts?.length} resort{filteredResorts?.length !== 1 ? 's' : ''} found
              </span>
              <div className="flex items-center bg-background rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded transition-organic ${
                    viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'text-text-secondary hover:text-primary'
                  }`}
                >
                  <Icon name="Grid3X3" size={16} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded transition-organic ${
                    viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'text-text-secondary hover:text-primary'
                  }`}
                >
                  <Icon name="List" size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Resorts Grid/List */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className={`grid gap-8 ${
            viewMode === 'grid' ?'grid-cols-1 md:grid-cols-2 xl:grid-cols-3' :'grid-cols-1'
          }`}>
            {filteredResorts?.map((resort) => (
              <div
                key={resort?.id}
                className={`bg-card rounded-2xl shadow-organic overflow-hidden hover-lift ${
                  selectedResort?.id === resort?.id ? 'ring-2 ring-primary ring-offset-2' : ''
                } ${viewMode === 'list' ? 'lg:flex lg:max-h-80' : ''}`}
              >
                {/* Image Gallery */}
                <div className={`relative ${
                  viewMode === 'list' ? 'lg:w-2/5 lg:flex-shrink-0' : 'h-56'
                } overflow-hidden`}>
                  <Image
                    src={resort?.images?.[0]}
                    alt={resort?.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-organic duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  
                  {/* Marketing Badge */}
                  <div className="absolute top-4 left-4 bg-accent text-accent-foreground px-3 py-1 rounded-full text-sm font-heading font-semibold">
                    {resort?.marketing?.split(' - ')?.[0]}
                  </div>

                  {/* Rating Badge */}
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full flex items-center">
                    <div className="flex items-center mr-2">
                      {renderStars(resort?.rating)}
                    </div>
                    <span className="text-sm font-heading font-semibold text-text-primary">
                      {resort?.rating}
                    </span>
                  </div>

                  {/* Price Badge */}
                  <div className="absolute bottom-4 left-4 bg-primary text-primary-foreground px-3 py-1 rounded-full">
                    <span className="text-sm font-heading font-bold">
                      {resort?.priceRange?.split(' - ')?.[0]}/night
                    </span>
                  </div>

                  {/* Image Count Indicator */}
                  <div className="absolute bottom-4 right-4 bg-black/60 text-white px-2 py-1 rounded-full text-xs">
                    1 / {resort?.images?.length}
                  </div>
                </div>

                {/* Content */}
                <div className={`p-6 ${viewMode === 'list' ? 'lg:flex-1 lg:flex lg:flex-col lg:justify-between' : ''}`}>
                  <div className={viewMode === 'list' ? 'lg:flex lg:justify-between lg:items-start lg:mb-4' : ''}>
                    <div className={viewMode === 'list' ? 'lg:flex-1 lg:mr-6' : ''}>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xl font-heading font-semibold text-text-primary">
                          {resort?.name}
                        </h3>
                        <div className="flex items-center text-sm text-text-secondary">
                          <Icon name="MapPin" size={14} className="mr-1" />
                          <span className="font-caption">{resort?.capacity}</span>
                        </div>
                      </div>
                      
                      <p className="text-text-secondary mb-4 font-body text-sm">
                        {viewMode === 'list' ? resort?.fullDescription : resort?.description}
                      </p>

                      {/* Key Features */}
                      <div className="mb-4">
                        <div className="grid grid-cols-2 gap-2">
                          {resort?.features?.slice(0, viewMode === 'list' ? 6 : 4)?.map((feature, index) => (
                            <div key={index} className="flex items-center text-xs text-text-secondary">
                              <Icon name="Check" size={10} className="text-success mr-1 flex-shrink-0" />
                              <span className="font-caption">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Awards and Certifications */}
                      {viewMode === 'list' && (
                        <div className="mb-4">
                          <h4 className="text-sm font-heading font-semibold text-text-primary mb-2">Awards & Certifications</h4>
                          <div className="flex flex-wrap gap-2">
                            {resort?.awards?.map((award, index) => (
                              <span key={index} className="bg-accent/10 text-accent px-2 py-1 rounded text-xs font-caption">
                                {award}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Pricing and Stats */}
                    {viewMode === 'list' && (
                      <div className="lg:text-right lg:flex-shrink-0">
                        <div className="text-2xl font-heading font-bold text-primary mb-1">
                          {resort?.priceRange}
                        </div>
                        <p className="text-sm text-text-secondary font-caption mb-2">per night</p>
                        <div className="text-sm text-text-secondary">
                          <div className="flex items-center justify-end mb-1">
                            <Icon name="Star" size={12} className="text-warning mr-1" />
                            <span>{resort?.rating} ({resort?.reviews} reviews)</span>
                          </div>
                          <div className="flex items-center justify-end">
                            <Icon name="MapPin" size={12} className="text-primary mr-1" />
                            <span>Est. {resort?.established}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Pricing for Grid View */}
                  {viewMode === 'grid' && (
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <span className="text-lg font-heading font-bold text-primary">
                          {resort?.priceRange}
                        </span>
                        <span className="text-sm text-text-secondary font-caption ml-1">per night</span>
                      </div>
                      <div className="text-sm text-text-secondary">
                        <div className="flex items-center">
                          <Icon name="Star" size={12} className="text-warning mr-1" />
                          <span className="font-caption">{resort?.rating} ({resort?.reviews})</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <Button
                        variant="default"
                        size="default"
                        iconName="Calendar"
                        iconPosition="left"
                        onClick={() => handleBookNow(resort)}
                        className="hover:scale-105 transition-organic"
                      >
                        Book Now
                      </Button>
                      <Button
                        variant="outline"
                        size="default"
                        iconName="Info"
                        iconPosition="left"
                        onClick={() => handleViewDetails(resort)}
                        className="border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground"
                      >
                        Details
                      </Button>
                    </div>
                    <Button
                      variant="outline"
                      size="default"
                      iconName="MessageCircle"
                      iconPosition="left"
                      fullWidth
                      onClick={() => handleWhatsAppCustom(resort)}
                      className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white"
                    >
                      WhatsApp Custom Quote
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Interactive Map Section */}
      <section className="py-16 bg-muted">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-text-primary mb-4">
              Resort Locations
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto font-body">
              Explore our resort locations and plan your perfect Dandeli adventure
            </p>
          </div>
          
          <div className="bg-card rounded-2xl shadow-organic p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Map Placeholder */}
              <div className="bg-muted rounded-lg h-96 flex items-center justify-center">
                <div className="text-center">
                  <Icon name="Map" size={48} className="text-text-secondary mx-auto mb-4" />
                  <p className="text-text-secondary font-body">Interactive Map Coming Soon</p>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Navigation"
                    iconPosition="left"
                    className="mt-4"
                    onClick={() => {
                      const searchQuery = encodeURIComponent("Dandeli Adventure Resorts Karnataka");
                      window.open(`https://www.google.com/maps/search/${searchQuery}`, '_blank');
                    }}
                  >
                    View on Google Maps
                  </Button>
                </div>
              </div>

              {/* Location Details */}
              <div>
                <h3 className="text-2xl font-heading font-semibold text-text-primary mb-6">
                  Getting Here
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="Car" size={20} className="text-primary mt-1" />
                    <div>
                      <h4 className="font-heading font-semibold text-text-primary">By Road</h4>
                      <p className="text-text-secondary font-body text-sm">
                        460km from Bangalore (8 hours) • 120km from Goa (3 hours)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Icon name="Plane" size={20} className="text-primary mt-1" />
                    <div>
                      <h4 className="font-heading font-semibold text-text-primary">By Air</h4>
                      <p className="text-text-secondary font-body text-sm">
                        Nearest airports: Hubli (75km) • Goa (120km) • Bangalore (460km)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Icon name="Train" size={20} className="text-primary mt-1" />
                    <div>
                      <h4 className="font-heading font-semibold text-text-primary">By Train</h4>
                      <p className="text-text-secondary font-body text-sm">
                        Dharwad Railway Station (58km) • Londa Junction (45km)
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 p-6 bg-primary/10 rounded-lg">
                  <h4 className="font-heading font-semibold text-primary mb-3">
                    🚗 Free Pickup Service Available
                  </h4>
                  <p className="text-text-secondary font-body text-sm mb-4">
                    Complimentary pickup from nearby transport hubs for confirmed bookings
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Phone"
                    iconPosition="left"
                    onClick={() => window.open('tel:+918277385225', '_self')}
                    className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                  >
                    Arrange Pickup
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Footer */}
      <Footer />
      {/* Floating Action Buttons */}
      <FloatingActionButtons />
    </div>
  );
};

export default ResortsOverview;